import type { Account } from "../types";
import { pct, num, money, money2, statusInfo, utcTime, ago } from "../format";
import { windowedStats, WINDOW_LABEL, type TfWindow } from "../perf";

const fmtPf = (v: number) => (v === Infinity ? "∞" : isFinite(v) ? v.toFixed(2) : "—");

function sinceLabel(start?: string | null): string | null {
  if (!start) return null;
  const d0 = new Date(start).getTime();
  if (!isFinite(d0)) return null;
  const days = Math.max(0, Math.round((Date.now() - d0) / 86400000));
  const date = new Date(start).toISOString().slice(0, 10);
  if (days < 60) return `${date} · ${days} days`;
  const mo = Math.round(days / 30.44);
  if (mo < 18) return `${date} · ${mo} months`;
  return `${date} · ${(days / 365).toFixed(1)} years`;
}
import PriceChart from "./PriceChart";
import EquityChart from "./EquityChart";
import PositionPanel from "./PositionPanel";
import TradesTable from "./TradesTable";
import ActivityLog from "./ActivityLog";

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="metric"><dt>{label}</dt><dd title={hint}>{value}</dd></div>
  );
}

export default function InstrumentView({ acct, window = "ALL" }: { acct: Account; window?: TfWindow }) {
  const m = acct.metrics || {};
  const s = statusInfo(acct.status);
  const params = Object.entries(acct.params || {}).map(([k, v]) => `${k}=${v}`).join("  ");
  const ws = windowedStats(acct, window);
  const scoped = window !== "ALL";

  return (
    <section aria-label={`${acct.instrument} detail`}>
      <header className="inst-head">
        <div>
          <h2>{acct.instrument} <span className="tf">· {acct.tf}</span></h2>
          <p className="params"><code>{acct.strategy} — {params}</code></p>
          {sinceLabel(acct.started_at) && (
            <p className="muted small">
              Paper-trading (out-of-sample) since {sinceLabel(acct.started_at)}
              {acct.n_trades_total ? ` · ${acct.n_trades_total} trades` : ""}
            </p>
          )}
          {acct.last_bar_ts && (
            <p className="muted small">
              Last closed bar {utcTime(acct.last_bar_ts)} ({ago(acct.last_bar_ts)})
            </p>
          )}
        </div>
        <span className={s.cls}><span className="visually-hidden">Status: </span><span aria-label={s.sr}>{s.label}</span></span>
      </header>

      <PositionPanel position={acct.position} />

      <h3>Price &amp; trade markers</h3>
      <PriceChart bars={acct.bars ?? []} trades={acct.trades ?? []} position={acct.position}
        label={`${acct.instrument} ${acct.tf}`} />

      {scoped && (
        <p className="muted small">
          Headline metrics and the equity curve below are scoped to the last {WINDOW_LABEL[window]}.
          Sharpe and max drawdown reflect the full history.
        </p>
      )}
      <dl className="metrics-grid wide">
        <Metric label="Trading since" value={acct.started_at ? new Date(acct.started_at).toISOString().slice(0, 10) : "—"}
          hint="first out-of-sample bar (model frozen at train-end)" />
        <Metric label={scoped ? "Trades (window)" : "Trades"} value={num(scoped ? ws.nTrades : m.n_trades, 0)} />
        <Metric label="Win rate" value={pct(scoped ? ws.wr : m.win_rate)} />
        <Metric label="Profit factor" value={scoped ? fmtPf(ws.pf) : num(m.profit_factor)} hint="gross win / gross loss" />
        <Metric label="Sharpe" value={num(m.sharpe)} hint="daily-resampled, annualized (full history)" />
        <Metric label="Max DD" value={pct(m.max_dd)} hint="full history" />
        <Metric label="Realized equity" value={money2(acct.realized_equity)} />
        <Metric label={scoped ? "P&L (window)" : "Total P&L"} value={money(scoped ? ws.pnl : m.total_pnl)} />
      </dl>

      <h3>Equity curve {scoped && <span className="muted small">— last {WINDOW_LABEL[window]}</span>}</h3>
      <EquityChart data={scoped ? ws.equitySlice : acct.equity} label={acct.instrument} height={180} />

      <h3>Trades — entries, exits &amp; prices
        {acct.n_trades_total ? ` (latest ${(acct.trades ?? []).length} of ${acct.n_trades_total})` : ""}</h3>
      <TradesTable trades={acct.trades ?? []} label={acct.instrument} />

      <h3>Activity log</h3>
      <ActivityLog events={acct.log ?? []} label={acct.instrument} />
    </section>
  );
}
